/**
 * Kartica »Zaključek meseca« na strani Uvoz (docs/ARHITEKTURA.md, 15.6):
 * mesec, ki je na vrsti, ali ga je mogoče zaključiti (in zakaj ne), seznam
 * zaključenih mesecev z Excelom meseca in odprtje zadnjega. Samo
 * administrator. Zaključek zamrzne odstotke do vključno meseca.
 */
import type { PregledZakljuckov } from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { mesecZaPrikaz } from '../lib/besedila-projektov';
import { datumInUra } from '../lib/format';

interface Lastnosti {
  podjetje: string;
  /** Števec sprememb podatkov (uvoz glavne knjige …); ob spremembi se stanje prebere znova. */
  osvezitev?: number;
}

export function ZakljucekMeseca({ podjetje, osvezitev = 0 }: Lastnosti): JSX.Element {
  const [pregled, setPregled] = useState<PregledZakljuckov | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [tece, setTece] = useState(false);

  const osvezi = useCallback((): void => {
    api
      .zakljucki(podjetje)
      .then((p) => {
        setPregled(p);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Zaključkov ni bilo mogoče prebrati.');
      });
  }, [podjetje]);
  useEffect(() => {
    osvezi();
  }, [osvezi]);
  const prva = useRef(osvezitev);
  useEffect(() => {
    if (osvezitev === prva.current) return;
    prva.current = osvezitev;
    osvezi();
  }, [osvezitev, osvezi]);

  const zakljuci = (mesec: string): void => {
    if (!window.confirm(`Zaključim mesec ${mesecZaPrikaz(mesec)}? Odstotki do vključno tega meseca bodo zamrznjeni, Excel meseca se shrani v arhiv.`)) return;
    setTece(true);
    setNapaka(null);
    api
      .zakljuciMesec(podjetje, mesec)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Meseca ni bilo mogoče zaključiti.');
      })
      .finally(() => {
        setTece(false);
      });
  };

  const odpri = (mesec: string): void => {
    if (!window.confirm(`Odprem mesec ${mesecZaPrikaz(mesec)}? Odstotki tega meseca se spet lahko spreminjajo, Excel meseca se odstrani.`)) return;
    setNapaka(null);
    api
      .odpriMesec(podjetje, mesec)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Meseca ni bilo mogoče odpreti.');
      });
  };

  return (
    <section className="kartica" aria-labelledby="zakljucek-meseca">
      <div className="glava-kartice">
        <h2 id="zakljucek-meseca">Zaključek meseca</h2>
      </div>
      <p className="podnaslov">
        Ko je temeljnica knjižena in glavna knjiga znova uvožena, mesec zaključi. Zaključek zamrzne odstotke do
        vključno tega meseca in shrani Excel meseca (odstotki, stanje sredstev, pregled projektov, knjižbe) v arhiv.
        Mesece se zaključuje po vrsti; odpreti je mogoče samo zadnji zaključen mesec.
      </p>
      {napaka !== null && (
        <p className="napaka" role="alert">
          {napaka}
        </p>
      )}
      {pregled !== null && pregled.naslednji !== null && (
        <div>
          {pregled.lahkoZakljuci ? (
            <button
              type="button"
              className="gumb"
              disabled={tece}
              onClick={() => {
                zakljuci(pregled.naslednji!);
              }}
            >
              Zaključi mesec {mesecZaPrikaz(pregled.naslednji)}
            </button>
          ) : (
            <>
              <p className="opozorilo">Meseca {mesecZaPrikaz(pregled.naslednji)} še ni mogoče zaključiti:</p>
              <ul>
                {pregled.razlogi.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {pregled !== null && pregled.naslednji === null && (
        <p className="drobno">
          {pregled.zakljucenoDo === null
            ? 'Ni še naloženega registra osnovnih sredstev.'
            : `Zaključeno do ${mesecZaPrikaz(pregled.zakljucenoDo)}. Naslednji mesec bo na vrsti, ko bo naložen njegov register.`}
        </p>
      )}
      {pregled !== null && pregled.zakljuceni.length > 0 && (
        <div className="tabela-ovoj">
          <table>
            <thead>
              <tr>
                <th>mesec</th>
                <th>zaključeno</th>
                <th>Excel meseca</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pregled.zakljuceni.map((z, i) => (
                <tr key={z.mesec}>
                  <td>{mesecZaPrikaz(z.mesec)}</td>
                  <td>
                    {datumInUra(z.zakljuceno)}
                    {z.zakljucil !== null && <span className="drobno"> ({z.zakljucil})</span>}
                  </td>
                  <td>
                    {z.datoteka === null ? (
                      '—'
                    ) : (
                      <a href={api.naslovZakljucka(podjetje, z.mesec)} download={z.imeDatoteke ?? undefined}>
                        {z.imeDatoteke}
                      </a>
                    )}
                  </td>
                  <td>
                    {i === 0 && (
                      <button
                        type="button"
                        className="gumb-besedilo"
                        onClick={() => {
                          odpri(z.mesec);
                        }}
                      >
                        odpri
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
